import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PERMISSIONS, defaultPermissions } from '../../utils/permissions';
import { TimeZoneSelect } from '../JobFieldInputs';
import { WorkLocationFields, workProblem } from './WorkLocationFields';
import {
  api, Avatar, btnDanger, btnPrimary, btnSecondary, card, EmptyState, InlineError, input, labelCls, Modal, Pagination,
  SidePanel, SkeletonRows, StatusPill, Tag, useToast,
} from '../ui/Kit';

export const CREATE_AND_RECRUIT = "A person can't both create jobs and recruit — the hiring manager and the recruiter of a job must be different people. Pick one, or make them a company admin.";

const PERM_LABEL = Object.fromEntries(PERMISSIONS.map((p) => [p.key, p.label]));
const DESIGNATIONS = ['Hiring Manager', 'Recruitment Manager', 'Recruitment Lead', 'Senior Recruiter', 'Recruiter',
  'Sourcing Recruiter', 'Interviewer', 'HR Business Partner', 'Finance'];

// ─── Add / edit side panel ───────────────────────────────────────────────────────────────────────────────────
export function MemberPanel({ open, member, setup, me, onClose, onSaved, onDeactivate }) {
  const toast = useToast();
  const creating = !member;
  const blank = { full_name: '', email: '', designation: '', department_id: '', level_id: '', reports_to_id: '', phone: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York', role: 'company_user', permissions: [],
    recruiter_capacity: '', work_mode: 'office', office_location_id: (setup.locations || []).length === 1 ? String(setup.locations[0].id) : '', home_country: '', home_state: '', home_city: '', hybrid_home: false };
  const [f, setF] = useState(blank);
  const [tzTouched, setTzTouched] = useState(false);        // Part E: the time zone follows the work location unless set by hand
  const [workTouched, setWorkTouched] = useState(false);
  const [follow, setFollow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [someoneElse, setSomeoneElse] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setError(''); setBusy(false);
    if (member) {
      setF({ full_name: member.full_name || '', email: member.email, designation: member.designation || '',
        department_id: member.department_id || '', level_id: member.level_id || '', reports_to_id: member.reports_to_id || '',
        phone: member.phone || '', timezone: member.timezone || 'America/New_York', role: member.role,
        permissions: member.is_admin ? [] : member.permissions, recruiter_capacity: member.recruiter_capacity || '',
        work_mode: member.work_mode || 'office', office_location_id: member.office_location_id || '', home_country: member.home_country || '',
        home_state: member.home_state || '', home_city: member.home_city || '', hybrid_home: !!(member.work_mode === 'hybrid' && member.home_city) });
      setTzTouched(false); setWorkTouched(false); setFollow(false);
      setTouched(true);
      const inDept = (setup.people || []).some((p) => p.id === member.reports_to_id && p.department_id === member.department_id);
      setSomeoneElse(!!member.reports_to_id && !inDept);
    } else { setF(blank); setTouched(false); setSomeoneElse(false); setTzTouched(false); setWorkTouched(false); setFollow(false); }
  }, [open, member]); // eslint-disable-line react-hooks/exhaustive-deps

  // the time zone shown follows the chosen work location (the server applies the same rule) until set by hand
  const office = (setup.locations || []).find((o) => String(o.id) === String(f.office_location_id));
  useEffect(() => {
    if (!open || tzTouched || !(workTouched || !member || follow)) return undefined;
    if (f.work_mode !== 'remote') { if (office && office.timezone) setF((x) => ({ ...x, timezone: office.timezone })); return undefined; }
    if (!f.home_country || !(f.home_city || '').trim()) return undefined;
    const t = setTimeout(() => {
      api(`/api/geo/timezone?country=${encodeURIComponent(f.home_country)}&state=${encodeURIComponent(f.home_state || '')}&city=${encodeURIComponent(f.home_city.trim())}`)
        .then((r) => { if (r && r.timezone) setF((x) => ({ ...x, timezone: r.timezone })); }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [open, tzTouched, workTouched, follow, f.work_mode, f.office_location_id, f.home_country, f.home_state, f.home_city, office]); // eslint-disable-line react-hooks/exhaustive-deps

  const people = (setup.people || []).filter((p) => !member || p.id !== member.id);
  const deptPeople = people.filter((p) => f.department_id && p.department_id === Number(f.department_id));
  const levelName = (id) => ((setup.levels || []).find((l) => l.id === id) || {}).name;
  const deptName = (id) => ((setup.departments || []).find((d) => d.id === id) || {}).name;
  const set = (k, v) => { if (['work_mode', 'office_location_id', 'home_country', 'home_state', 'home_city', 'hybrid_home'].includes(k)) setWorkTouched(true); setF((x) => ({ ...x, [k]: v })); };
  const isAdmin = f.role === 'company_super_admin';
  const self = member && me && member.id === me.id;

  const save = async () => {
    setError('');
    if (!f.full_name.trim()) { setError('Enter the person\'s full name'); return; }
    if (creating && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) { setError('Enter a valid e-mail address'); return; }
    const problem = (creating || workTouched) ? workProblem(f, (setup.locations || []).length > 0) : '';
    if (problem) { setError(problem); return; }
    setBusy(true);
    const body = { full_name: f.full_name.trim(), designation: f.designation || null, phone: f.phone || null,
      department_id: f.department_id ? Number(f.department_id) : null, level_id: f.level_id ? Number(f.level_id) : null,
      reports_to_id: f.reports_to_id ? Number(f.reports_to_id) : null };
    if (tzTouched) body.timezone = f.timezone || null;
    if (workTouched || (creating && (f.work_mode !== 'office' || f.office_location_id))) {       // (a new office person with no office chosen: leave it to the admin)
      body.work_mode = f.work_mode; body.office_location_id = f.work_mode === 'remote' ? null : (f.office_location_id ? Number(f.office_location_id) : null);
      const home = f.work_mode === 'remote' || (f.work_mode === 'hybrid' && f.hybrid_home);
      body.home_country = home ? (f.home_country || null) : null; body.home_state = home ? (f.home_state || null) : null; body.home_city = home ? (f.home_city || null) : null;
    }
    if (follow) body.follow_location_timezone = true;
    if (!self) body.role = f.role;
    if (!isAdmin) body.permissions = f.permissions;
    if (isAdmin || f.permissions.includes('recruit')) body.recruiter_capacity = f.recruiter_capacity ? Number(f.recruiter_capacity) : null;
    try {
      if (creating) {
        const r = await api('/api/org/members', { method: 'POST', body: { ...body, email: f.email.trim() } });
        toast.show(r.invite_email === 'sent' ? `Invite sent to ${r.member.email}` : `${r.member.full_name} added — the invite e-mail could not be sent`,
          r.invite_email === 'sent' ? 'ok' : 'error');
      } else {
        await api(`/api/org/members/${member.id}`, { method: 'PATCH', body });
        toast.show('Changes saved');
      }
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  };
  const resend = async () => {
    try { const r = await api(`/api/org/members/${member.id}/resend-invite`, { method: 'POST' });
      toast.show(r.invite_email === 'sent' ? 'A new invite was sent' : 'The invite e-mail could not be sent', r.invite_email === 'sent' ? 'ok' : 'error'); }
    catch (e) { setError(e.message); }
  };
  const clearBounce = async () => {
    try { await api(`/api/org/members/${member.id}/email-bounce`, { method: 'DELETE' }); toast.show('E-mail will be sent to this address again'); onSaved(); }
    catch (e) { setError(e.message); }
  };
  const reactivate = async () => {
    try { await api(`/api/org/members/${member.id}/reactivate`, { method: 'POST' }); toast.show(`${member.full_name} is active again`); onSaved(); }
    catch (e) { setError(e.message); }
  };

  return (
    <SidePanel open={open} onClose={onClose} title={creating ? 'Add user' : 'Edit user'}
      subtitle={creating ? 'They get an e-mail invite to set their password.' : member && `${member.full_name} · ${member.email}`}
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} onClick={save} disabled={busy}>{busy ? 'Saving…' : creating ? 'Send invite' : 'Save changes'}</button></>}>
      <div className="space-y-4">
        <InlineError>{error}</InlineError>
        {member && member.email_bouncing && (
          <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
            E-mails to this address bounced, so Quali5d stopped sending them.{' '}
            <button className="font-semibold underline" onClick={clearBounce}>Send to it again</button>
          </div>
        )}
        <label className="block"><span className={labelCls}>Full name</span>
          <input className={input} value={f.full_name} onChange={(e) => set('full_name', e.target.value)} /></label>
        {creating && <label className="block"><span className={labelCls}>Work e-mail</span>
          <input className={input} type="email" value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="name@company.com" /></label>}
        <label className="block"><span className={labelCls}>Designation (shown on their profile)</span>
          <input className={input} list="designation-options" value={f.designation}
            onChange={(e) => { const v = e.target.value; setF((x) => ({ ...x, designation: v, ...(touched ? {} : { permissions: defaultPermissions(v) }) })); }} />
          <datalist id="designation-options">{DESIGNATIONS.map((d) => <option key={d} value={d} />)}</datalist></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Department</span>
            <select className={input} value={f.department_id} onChange={(e) => { set('department_id', e.target.value); if (!someoneElse) set('reports_to_id', ''); }}>
              <option value="">{(setup.departments || []).length ? 'Choose…' : 'No departments yet'}</option>
              {(setup.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></label>
          <label className="block"><span className={labelCls}>Level</span>
            <select className={input} value={f.level_id} onChange={(e) => set('level_id', e.target.value)}>
              <option value="">Choose…</option>
              {(setup.levels || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></label>
        </div>
        <label className="block"><span className={labelCls}>Reports to</span>
          {someoneElse ? (
            <select className={input} aria-label="Reports to (anyone)" aria-describedby="reports-hint" value={f.reports_to_id} onChange={(e) => set('reports_to_id', e.target.value)}>
              <option value="">Nobody</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          ) : (
            <select className={input} aria-describedby="reports-hint" value={f.reports_to_id} onChange={(e) => { if (e.target.value === '__else') { setSomeoneElse(true); set('reports_to_id', ''); } else set('reports_to_id', e.target.value); }}>
              <option value="">Nobody</option>
              {deptPeople.map((p) => (
                <option key={p.id} value={p.id}>{[p.full_name, [deptName(p.department_id), levelName(p.level_id)].filter(Boolean).join(' · ')].filter(Boolean).join(' — ')}</option>
              ))}
              <option value="__else">Someone else…</option>
            </select>
          )}
          </label>
        <p id="reports-hint" className="-mt-3 text-xs text-[#64748B]">Builds the org chart and "HM's manager" approval steps</p>
        <label className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]">
          <input type="checkbox" checked={isAdmin} disabled={self} onChange={(e) => set('role', e.target.checked ? 'company_super_admin' : 'company_user')} />
          Company admin <span className="font-normal text-[#64748B]">— manages people, org setup and workflows</span></label>
        <fieldset>
          <legend className={labelCls}>Permissions</legend>
          {isAdmin ? <p className="text-sm text-[#64748B]">Company admins have every permission.</p> : (
            <div className="space-y-2">
              {PERMISSIONS.map((p) => (
                <label key={p.key} className="flex items-start gap-2.5 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={f.permissions.includes(p.key)}
                    onChange={(e) => {
                      setTouched(true);
                      const other = p.key === 'create_jobs' ? 'recruit' : p.key === 'recruit' ? 'create_jobs' : null;
                      if (e.target.checked && other && f.permissions.includes(other)) { setError(CREATE_AND_RECRUIT); return; }
                      setError('');
                      set('permissions', e.target.checked ? [...f.permissions, p.key] : f.permissions.filter((k) => k !== p.key));
                    }} />
                  <span><span className="font-semibold text-[#0F172A]">{p.label}</span><span className="block text-xs text-[#64748B]">{p.hint}</span></span>
                </label>
              ))}
            </div>
          )}
        </fieldset>
        {(isAdmin || f.permissions.includes('recruit')) && (
          <label className="block"><span className={labelCls}>Recruiter capacity (open jobs)</span>
            <input className={input + ' !w-32'} inputMode="numeric" placeholder="6" value={f.recruiter_capacity}
              onChange={(e) => set('recruiter_capacity', e.target.value.replace(/[^0-9]/g, ''))} />
            <span className="block text-xs text-[#64748B] mt-1">Used for the workload bars in Team jobs. Empty = 6.</span></label>
        )}
        <WorkLocationFields f={f} set={set} offices={setup.locations} />
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={labelCls}>Phone (optional)</span>
            <input className={input} value={f.phone} onChange={(e) => set('phone', e.target.value)} /></label>
          <label className="block"><span className={labelCls}>Time zone</span>
            <TimeZoneSelect className={input} value={f.timezone} onChange={(v) => { setTzTouched(true); setFollow(false); setF((x) => ({ ...x, timezone: v })); }} allowEmpty={false} />
            <span className="block text-xs text-[#64748B] mt-1" data-testid="member-zone-origin">{tzTouched ? 'Set by you' : (member ? member.timezone_origin : 'Follows the work location')}
              {member && !tzTouched && member.timezone_source === 'manual' && !follow && (
                <> · <button type="button" className="font-semibold text-[#0066CC] hover:underline" onClick={() => { setFollow(true); setWorkTouched(true); }} data-testid="tz-follow">use the work location’s</button></>)}
            </span></label>
        </div>
        {member && (
          <div className="pt-3 border-t border-[#E6E9F0] flex flex-wrap gap-2">
            {member.status === 'invited' && <button className={btnSecondary} onClick={resend}>Resend invite</button>}
            {member.status !== 'inactive' && !member.is_admin && !self && <button className={btnSecondary + ' !text-red-700'} onClick={() => onDeactivate(member)}>Deactivate…</button>}
            {member.status === 'inactive' && <button className={btnSecondary} onClick={reactivate}>Reactivate</button>}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

// ─── Deactivate with mandatory reassignment ─────────────────────────────────────────────────────────────────────
export function DeactivateDialog({ member, onClose, onDone }) {
  const toast = useToast();
  const [info, setInfo] = useState(null);
  const [targets, setTargets] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!member) return;
    setInfo(null); setTargets({}); setError('');
    api(`/api/org/members/${member.id}/open-items`).then(setInfo).catch((e) => setError(e.message));
  }, [member]);
  const required = info ? info.kinds.filter((k) => k.required) : [];
  const ready = info && required.every((k) => targets[k.key]);
  const go = async () => {
    setBusy(true); setError('');
    try {
      await api(`/api/org/members/${member.id}/deactivate`, { method: 'POST', body: { targets } });
      toast.show(`${member.full_name} was deactivated and their work handed over`);
      onDone();
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open={!!member} onClose={onClose} title={member ? `Deactivate ${member.full_name}` : ''} width="max-w-xl"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnDanger} disabled={!ready || busy} onClick={go}>{busy ? 'Deactivating…' : 'Deactivate and reassign'}</button></>}>
      <div className="space-y-4 text-sm">
        <p className="text-[#334155]">They can no longer sign in. Nothing is deleted — every job and approval they own moves to the people you choose, and the job timelines record the hand-over.</p>
        <InlineError>{error}</InlineError>
        {!info && !error && <p className="text-[#64748B]">Checking their open work…</p>}
        {info && !required.length && <p className="text-[#334155]">They have no open jobs or approvals.</p>}
        {required.map((k) => (
          <div key={k.key} className="border border-[#E6E9F0] rounded-lg p-3">
            <p className="font-semibold text-[#0F172A]">{k.label}{k.items.length ? ` · ${k.items.length}` : ''}</p>
            {k.items.length > 0 && <p className="text-xs text-[#64748B] mt-0.5">{k.items.slice(0, 4).map((i) => i.req_id || i.job_title).join(', ')}{k.items.length > 4 ? ` +${k.items.length - 4}` : ''}</p>}
            {k.routing_role && <p className="text-xs text-[#64748B] mt-0.5">Also their role in the approval routing.</p>}
            <label className="block mt-2"><span className="sr-only">New owner: {k.label}</span>
              <select className={input} value={targets[k.key] || ''} onChange={(e) => setTargets({ ...targets, [k.key]: Number(e.target.value) || undefined })}>
                <option value="">Hand over to…</option>
                {k.candidates.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select></label>
            {!k.candidates.length && <p className="text-xs text-red-700 mt-1">Nobody else has the "{PERM_LABEL[k.permission]}" permission — give it to someone first.</p>}
          </div>
        ))}
        {info && info.reports.length > 0 && <p className="text-[#334155]">{info.reports.length} direct report{info.reports.length > 1 ? 's' : ''} will report to {info.reports_move_to ? info.reports_move_to.full_name : 'nobody'}.</p>}
        {info && info.department_heads.length > 0 && <p className="text-[#334155]">{info.department_heads.join(', ')} will have no head until you choose one in Org setup.</p>}
      </div>
    </Modal>
  );
}

// ─── CSV import: template, preview with errors, all-or-nothing import ───────────────────────────────────────────
export function CsvImport({ open, onClose, onDone }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setText(''); setName(''); setPreview(null); setError(''); setBusy(false); } }, [open]);
  const template = async () => {
    try {
      const res = await api('/api/org/import/template', { raw: true });
      const blob = new Blob([await res.text()], { type: 'text/csv' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'quali5d_people_template.csv'; a.click();
    } catch (e) { setError(e.message); }
  };
  const pick = async (file) => {
    setError(''); setPreview(null);
    if (!file) return;
    if (file.size > 1000000) { setError('The file is too large (1 MB max)'); return; }
    const t = await file.text();
    setText(t); setName(file.name); setBusy(true);
    try { setPreview(await api('/api/org/import/preview', { method: 'POST', body: { csv: t } })); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const commit = async () => {
    setBusy(true); setError('');
    try {
      const r = await api('/api/org/import/commit', { method: 'POST', body: { csv: text } });
      toast.show(`${r.created} people added · ${r.invites_sent} invites sent${r.invites_not_sent ? ` · ${r.invites_not_sent} not sent` : ''}`);
      onDone();
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Import people from CSV" width="max-w-3xl"
      footer={<><button className={btnSecondary} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} disabled={!preview || preview.invalid > 0 || busy} onClick={commit}>
          {busy && preview ? 'Importing…' : preview ? `Import ${preview.valid} people` : 'Import'}</button></>}>
      <div className="space-y-4 text-sm">
        <p className="text-[#334155]">One person per row: <code>full_name, email</code> (required), then optionally designation, department, level, reports_to_email, permissions (e.g. "Create jobs; Interview"), role, phone, timezone. Everyone gets an e-mail invite. Nothing is imported unless every row is valid.</p>
        <div className="flex flex-wrap gap-2 items-center">
          <button className={btnSecondary} onClick={template}>Download template</button>
          <label className={btnSecondary + ' cursor-pointer'}>Choose CSV file
            <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => pick(e.target.files && e.target.files[0])} /></label>
          {name && <span className="text-[#64748B]">{name}</span>}
        </div>
        <InlineError>{error}</InlineError>
        {busy && !preview && <p className="text-[#64748B]">Checking the file…</p>}
        {preview && (
          <>
            <p className={preview.invalid ? 'text-red-700 font-semibold' : 'text-[#2F7A47] font-semibold'}>
              {preview.valid} ready · {preview.invalid} with errors{preview.invalid ? ' — fix them in the file and choose it again' : ''}</p>
            <div className="border border-[#E6E9F0] rounded-lg overflow-auto max-h-[45vh]">
              <table className="w-full text-xs">
                <thead className="bg-[#F5F7FB] text-[#64748B] sticky top-0"><tr>
                  {['Line', 'Name', 'E-mail', 'Department', 'Level', 'Reports to', 'Permissions', 'Problems'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2">{h}</th>)}
                </tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.line} className={`border-t border-[#E6E9F0] ${r.errors.length ? 'bg-red-50/60' : ''}`}>
                      <td className="px-3 py-2 text-[#64748B]">{r.line}</td><td className="px-3 py-2">{r.full_name}</td><td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.department}</td><td className="px-3 py-2">{r.level}</td><td className="px-3 py-2">{r.reports_to_email}</td>
                      <td className="px-3 py-2">{r.permissions ? r.permissions.map((p) => PERM_LABEL[p]).join(', ') : <span className="text-[#94A3B8]">from designation</span>}</td>
                      <td className="px-3 py-2 text-red-700">{r.errors.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Org chart (read-only) ───────────────────────────────────────────────────────────────────────────────────────
export function OrgChart() {
  const [people, setPeople] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/org/chart').then((r) => setPeople(r.people)).catch((e) => setError(e.message)); }, []);
  const tree = useMemo(() => {
    if (!people) return [];
    const ids = new Set(people.map((p) => p.id));
    const kids = {};
    people.forEach((p) => { const k = p.reports_to_id && ids.has(p.reports_to_id) ? p.reports_to_id : 'root'; (kids[k] = kids[k] || []).push(p); });
    return kids;
  }, [people]);
  if (error) return <div className="p-4"><InlineError>{error}</InlineError></div>;
  if (!people) return <p className="p-6 text-sm text-[#64748B]">Loading the org chart…</p>;
  const Node = ({ p, depth }) => (
    <li>
      <div className="inline-flex items-center gap-2.5 bg-white border border-[#E6E9F0] rounded-lg px-3 py-2 my-1">
        <Avatar name={p.full_name} size="sm" />
        <span><span className="block text-sm font-semibold">{p.full_name}</span>
          <span className="block text-xs text-[#64748B]">{[p.designation, p.department, p.level].filter(Boolean).join(' · ')}</span></span>
        {p.status === 'invited' && <StatusPill status="invited" />}
      </div>
      {(tree[p.id] || []).length > 0 && depth < 12 && (
        <ul className="ml-6 pl-4 border-l border-[#E6E9F0]">{tree[p.id].map((k) => <Node key={k.id} p={k} depth={depth + 1} />)}</ul>
      )}
    </li>
  );
  return (
    <div className="p-5 overflow-x-auto" aria-label="Org chart">
      {!(tree.root || []).length ? <EmptyState title="Nobody yet" /> : <ul>{tree.root.map((p) => <Node key={p.id} p={p} depth={0} />)}</ul>}
      <p className="text-xs text-[#64748B] mt-3">Set "Reports to" on each person to build the chart.</p>
    </div>
  );
}

// ─── The tab ─────────────────────────────────────────────────────────────────────────────────────────────────────
export default function TeamTab({ me }) {
  const [setup, setSetup] = useState({ departments: [], levels: [], people: [] });
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [perm, setPerm] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [view, setView] = useState('table');
  const [panel, setPanel] = useState(null);           // null | 'new' | member
  const [deactivating, setDeactivating] = useState(null);
  const [importing, setImporting] = useState(false);
  const PAGE = 10;

  const loadSetup = useCallback(() => api('/api/org/setup').then(setSetup).catch(() => {}), []);
  const load = useCallback(async () => {
    setError('');
    const qs = new URLSearchParams({ page: String(page), page_size: String(PAGE) });
    if (q.trim()) qs.set('q', q.trim());
    if (dept) qs.set('department_id', dept);
    if (perm) qs.set('permission', perm);
    if (status) qs.set('status', status);
    try { setList(await api(`/api/org/members?${qs}`)); } catch (e) { setError(e.message); setList({ items: [], total: 0, total_all: 0 }); }
  }, [q, dept, perm, status, page]);
  useEffect(() => { loadSetup(); }, [loadSetup]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [q, dept, perm, status]);
  const refresh = () => { setPanel(null); load(); loadSetup(); };
  const filtered = q || dept || perm || status;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input aria-label="Search people" className={input + ' !w-64'} placeholder="Search people" value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label="Department filter" className={input + ' !w-auto'} value={dept} onChange={(e) => setDept(e.target.value)}>
          <option value="">All departments</option>
          {setup.departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select aria-label="Permission filter" className={input + ' !w-auto'} value={perm} onChange={(e) => setPerm(e.target.value)}>
          <option value="">All permissions</option>
          {PERMISSIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select aria-label="Status filter" className={input + ' !w-auto'} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option><option value="active">Active</option><option value="invited">Invited</option><option value="inactive">Inactive</option>
        </select>
        <div className="ml-auto flex gap-2">
          <button className={btnSecondary} aria-pressed={view === 'chart'} onClick={() => setView(view === 'chart' ? 'table' : 'chart')}>{view === 'chart' ? 'Table' : 'Org chart'}</button>
          <button className={btnSecondary} onClick={() => setImporting(true)}>Import CSV</button>
          <button className={btnPrimary} onClick={() => setPanel('new')}>+ Add user</button>
        </div>
      </div>
      <InlineError>{error}</InlineError>
      {list && (list.no_office || []).length > 0 && (
        <p className="text-xs text-[#64748B]" data-testid="no-office-list">No office — assign one: {list.no_office.map((p, i) => (
          <span key={p.id}>{i ? ', ' : ''}<button type="button" className="text-[#0066CC] font-semibold hover:underline" onClick={() => setPanel(list.items.find((u) => u.id === p.id) || p)}>{p.full_name}</button></span>))}</p>)}
      <div className={card + ' overflow-hidden'}>
        {view === 'chart' ? <OrgChart /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[#64748B] text-xs">
                  <tr>{['Name', 'Department', 'Level', 'Reports to', 'Permissions', 'Status', ''].map((h, i) => (
                    <th key={i} scope="col" className="text-left font-semibold px-4 py-3">{h || <span className="sr-only">Actions</span>}</th>))}</tr>
                </thead>
                {list === null ? <SkeletonRows cols={7} /> : (
                  <tbody>
                    {list.items.map((u) => (
                      <tr key={u.id} className={`border-t border-[#E6E9F0] ${u.status === 'inactive' ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-3"><div className="flex items-center gap-3"><Avatar name={u.full_name} />
                          <div className="min-w-0"><p className="font-semibold text-[#0F172A] truncate">{u.full_name}</p><p className="text-xs text-[#64748B] truncate">{u.email}</p>
                            <p className="text-[11px] text-[#64748B] truncate" data-testid={`work-tag-${u.id}`}>{u.needs_office ? <span className="text-[#B45309]">No office — assign one</span> : u.work_tag}</p></div></div></td>
                        <td className="px-4 py-3 text-[#334155]">{u.department || <span className="text-[#94A3B8]">—</span>}</td>
                        <td className="px-4 py-3 text-[#334155]">{u.level || <span className="text-[#94A3B8]">—</span>}</td>
                        <td className="px-4 py-3 text-[#334155]">{u.reports_to || <span className="text-[#94A3B8]">—</span>}</td>
                        <td className="px-4 py-3"><div className="flex flex-wrap gap-1 max-w-[260px]" data-testid={`perms-${u.id}`}>
                          {u.is_admin ? <Tag tone="teal">Company admin</Tag> : u.permissions.length ? u.permissions.map((p) => <Tag key={p}>{PERM_LABEL[p]}</Tag>) : <span className="text-[#94A3B8] text-xs">None</span>}</div></td>
                        <td className="px-4 py-3"><div className="flex flex-col items-start gap-1"><StatusPill status={u.status} />{u.email_bouncing && <Tag tone="red">E-mail bouncing</Tag>}</div></td>
                        <td className="px-4 py-3 text-right"><button className="text-[#0066CC] font-semibold text-sm hover:underline" onClick={() => setPanel(u)} aria-label={`Edit ${u.full_name}`}>Edit</button></td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
            {list && !list.items.length && (filtered
              ? <EmptyState title="No one matches" hint="Try another search or clear the filters." />
              : <EmptyState title="No people yet" hint="Add your team one by one or import a CSV." action={<button className={btnPrimary} onClick={() => setPanel('new')}>+ Add user</button>} />)}
            {list && list.total > 0 && <Pagination page={page} pageSize={PAGE} total={list.total} count={list.items.length} onPage={setPage} />}
          </>
        )}
      </div>
      <MemberPanel open={!!panel} member={panel === 'new' ? null : panel} setup={setup} me={me} onClose={() => setPanel(null)}
        onSaved={refresh} onDeactivate={(m) => { setPanel(null); setDeactivating(m); }} />
      <DeactivateDialog member={deactivating} onClose={() => setDeactivating(null)} onDone={() => { setDeactivating(null); refresh(); }} />
      <CsvImport open={importing} onClose={() => setImporting(false)} onDone={() => { setImporting(false); refresh(); }} />
    </div>
  );
}
